import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Resolve a search-param tab value against the tabs the user may see. */
export function resolveTabParam<T extends string>(
  raw: string | null,
  allowedTabs: readonly T[],
  defaultTab: T,
): T {
  if (raw && (allowedTabs as readonly string[]).includes(raw)) {
    return raw as T;
  }
  return defaultTab;
}

/**
 * Sync an active tab (or similar view key) to a URL search param.
 * Invalid / gated values fall back to `defaultTab`.
 * Other search params are preserved when updating.
 */
export function useTabSearchParam<T extends string>(
  allowedTabs: readonly T[],
  defaultTab: T,
  paramName = 'tab',
): [T, (tab: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(
    () => resolveTabParam(searchParams.get(paramName), allowedTabs, defaultTab),
    [searchParams, paramName, allowedTabs, defaultTab],
  );

  const setTab = useCallback(
    (tab: T) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, tab);
          return next;
        },
        { replace: false },
      );
    },
    [paramName, setSearchParams],
  );

  return [activeTab, setTab];
}
